import * as React from "react";
import { RefreshDataButtonProps } from "../../types/stripe";

/**
 * RefreshDataButton Component
 * 
 * A smart refresh button with auto-refresh capabilities, last refreshed timestamp,
 * visual feedback during refresh operations, and cache clearing functionality.
 */
const RefreshDataButton: React.FC<RefreshDataButtonProps> = ({
  onRefresh,
  onClearCache,
  isRefreshing = false,
  disabled = false,
  autoRefresh = false,
  refreshInterval = 30, // seconds
  lastRefreshed,
  size = 'md',
  variant = 'outline',
  className = "",
  id
}) => {
  const [countdown, setCountdown] = React.useState<number>(0);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const countdownRef = React.useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Auto-refresh functionality
  React.useEffect(() => {
    if (autoRefresh && !disabled && !isRefreshing) {
      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Set up auto-refresh interval
      intervalRef.current = setInterval(() => {
        onRefresh();
      }, refreshInterval * 1000);

      // Set up countdown timer
      setCountdown(refreshInterval);
      const startCountdown = () => {
        countdownRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              return refreshInterval;
            }
            return prev - 1;
          });
        }, 1000);
      };

      startCountdown();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
        }
      };
    } else {
      // Clean up intervals when auto-refresh is disabled
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      setCountdown(0);
    }
  }, [autoRefresh, disabled, isRefreshing, onRefresh, refreshInterval]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleRefresh = () => {
    if (!disabled && !isRefreshing) {
      onRefresh();
      // Reset countdown when manually refreshed
      if (autoRefresh) {
        setCountdown(refreshInterval);
      }
      setShowDropdown(false);
    }
  };

  const handleClearCache = () => {
    if (!disabled && !isRefreshing && onClearCache) {
      onClearCache();
      setShowDropdown(false);
    }
  };

  const formatLastRefreshed = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const formatCountdown = (seconds: number): string => {
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  };

  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline-secondary'
  };

  const getTooltipContent = (): string => {
    const parts = [];
    
    if (lastRefreshed) {
      parts.push(`Last refreshed: ${formatLastRefreshed(lastRefreshed)}`);
    }
    
    if (autoRefresh && countdown > 0) {
      parts.push(`Next refresh in: ${formatCountdown(countdown)}`);
    }
    
    if (isRefreshing) {
      parts.push('Refreshing data...');
    } else if (disabled) {
      parts.push('Refresh disabled');
    } else {
      parts.push('Click to refresh data');
    }

    return parts.join('\n');
  };

  return (
    <div className={`refresh-data-button-container ${className}`.trim()} id={id} ref={dropdownRef}>
      {onClearCache ? (
        // Button group with dropdown
        <div className="btn-group">
          <button
            type="button"
            className={`btn ${variantClasses[variant]} ${sizeClasses[size]} d-flex align-items-center`}
            onClick={handleRefresh}
            disabled={disabled || isRefreshing}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            title={getTooltipContent()}
          >
            {/* Refresh Icon */}
            <div className="refresh-icon-container me-2">
              <i 
                className={`fas fa-sync-alt ${isRefreshing ? 'fa-spin' : ''}`} 
                aria-hidden="true"
              ></i>
            </div>

            {/* Button Text */}
            <span>
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </span>
          </button>
          <button
            type="button"
            className={`btn ${variantClasses[variant]} ${sizeClasses[size]} dropdown-toggle dropdown-toggle-split`}
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={disabled || isRefreshing}
            aria-expanded={showDropdown}
          >
            <span className="visually-hidden">Toggle Dropdown</span>
          </button>
          {showDropdown && (
            <div className="dropdown-menu show" style={{ position: 'absolute', top: '100%', right: 0 }}>
              <button className="dropdown-item" type="button" onClick={handleRefresh}>
                <i className="fas fa-sync-alt me-2" aria-hidden="true"></i>
                Refresh Data
              </button>
              <div className="dropdown-divider"></div>
              <button className="dropdown-item text-danger" type="button" onClick={handleClearCache}>
                <i className="fas fa-trash-alt me-2" aria-hidden="true"></i>
                Clear Cache & Refresh
              </button>
            </div>
          )}
        </div>
      ) : (
        // Simple button without dropdown
        <button
          type="button"
          className={`btn ${variantClasses[variant]} ${sizeClasses[size]} d-flex align-items-center position-relative`}
          onClick={handleRefresh}
          disabled={disabled || isRefreshing}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          title={getTooltipContent()}
        >
          {/* Refresh Icon */}
          <div className="refresh-icon-container me-2">
            <i 
              className={`fas fa-sync-alt ${isRefreshing ? 'fa-spin' : ''}`} 
              aria-hidden="true"
            ></i>
          </div>

          {/* Button Text */}
          <span>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </span>
        </button>
      )}


      {/* Tooltip */}
      {showTooltip && (
        <div className="tooltip-container">
          <div className="tooltip-content">
            {getTooltipContent().split('\n').map((line, index) => (
              <div key={index} className={index === 0 ? 'fw-bold' : ''}>
                {line}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last refreshed indicator */}
      {lastRefreshed && !showTooltip && (
        <div className="last-refreshed-indicator">
          <small className="text-muted">
            Updated {formatLastRefreshed(lastRefreshed)}
          </small>
        </div>
      )}

      <style>{`
        .refresh-data-button-container {
          position: relative;
          display: inline-block;
        }

        .refresh-icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .fa-spin {
          animation: fa-spin 1s infinite linear;
        }

        @keyframes fa-spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .auto-refresh-indicator {
          display: flex;
          align-items: center;
        }

        .countdown-progress {
          position: absolute;
          top: 50%;
          right: 8px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .tooltip-container {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 8px;
          z-index: 1000;
          animation: fadeInUp 0.2s ease-out;
        }

        .tooltip-content {
          background: rgba(0, 0, 0, 0.9);
          color: white;
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 0.875rem;
          line-height: 1.4;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .tooltip-content::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 4px solid transparent;
          border-top-color: rgba(0, 0, 0, 0.9);
        }

        .last-refreshed-indicator {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 4px;
          white-space: nowrap;
        }

        /* Button states */
        .btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .btn:disabled .fa-spin {
          animation-duration: 0.8s;
        }

        /* Responsive adjustments */
        @media (max-width: 576px) {
          .btn {
            padding: 0.5rem 1rem;
          }
          
          .auto-refresh-indicator small {
            display: none;
          }
          
          .countdown-progress {
            right: 4px;
          }
          
          .tooltip-content {
            font-size: 0.75rem;
            padding: 6px 8px;
          }
        }

        /* Focus and accessibility */
        .btn:focus {
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .auto-refresh-indicator .fa-circle {
            color: var(--bs-success) !important;
          }
          
          .tooltip-content {
            background: var(--bs-dark);
            border: 2px solid var(--bs-light);
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .fa-spin {
            animation: none;
          }
          
          .countdown-progress circle {
            transition: none;
          }
          
          .tooltip-container {
            animation: none;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

RefreshDataButton.displayName = 'RefreshDataButton';

export default RefreshDataButton;